import { MouseEvent, useEffect, useState } from "react";

import { MdArrowBackIos, MdArrowForwardIos } from "react-icons/md";

import { cn } from "@/utils/cn";

interface PaginationProps {
  count: number;
  page: number;
  onChange: (page: number) => void;
}

const Pagination = ({ count, page, onChange }: PaginationProps) => {
  const [displayPages, setDisplayPages] = useState<number[]>([]);

  const isDisabledPreviousButton = count === 0 || displayPages[0] === 1;
  const isDisabledNextButton =
    count === 0 || displayPages[displayPages.length - 1] === count;

  const goPreviousPages = () => {
    const previousPages = Array.from(
      { length: 5 },
      (_, i) => displayPages[0] - 5 + i,
    );
    setDisplayPages(previousPages);
    onChange(previousPages[previousPages.length - 1] - 1);
  };

  const goNextPages = () => {
    const nextPages = displayPages
      .map(displayPage => displayPage + 5)
      .filter(displayPage => displayPage <= count);
    setDisplayPages(nextPages);
    onChange(nextPages[0] - 1);
  };

  const handleClickPageButton = (e: MouseEvent<HTMLButtonElement>) => {
    onChange(Number(e.currentTarget.dataset.page) - 1);
  };

  useEffect(() => {
    const initialDisplayPages = Array.from(
      { length: Math.min(5, count) },
      (_, i) => i + 1,
    );
    setDisplayPages(initialDisplayPages);
  }, [count]);

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        disabled={isDisabledPreviousButton}
        onClick={goPreviousPages}
        className={cn(
          "flex h-8 w-8 items-center justify-center rounded-lg transition-colors",
          isDisabledPreviousButton
            ? "cursor-not-allowed text-gray-300"
            : "text-gray-500 hover:bg-gray-100",
        )}
      >
        <MdArrowBackIos size={12} />
      </button>

      {displayPages.map(displayPage => {
        const isActive = displayPage === page + 1;
        return (
          <button
            key={displayPage}
            type="button"
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-lg text-sm transition-all duration-150",
              isActive
                ? "bg-primary font-semibold text-white shadow-sm"
                : "font-light text-gray-500 hover:-translate-y-px hover:bg-gray-100 hover:shadow-sm",
            )}
            data-page={displayPage}
            onClick={handleClickPageButton}
          >
            {displayPage}
          </button>
        );
      })}

      <button
        type="button"
        disabled={isDisabledNextButton}
        onClick={goNextPages}
        className={cn(
          "flex h-8 w-8 items-center justify-center rounded-lg transition-colors",
          isDisabledNextButton
            ? "cursor-not-allowed text-gray-300"
            : "text-gray-500 hover:bg-gray-100",
        )}
      >
        <MdArrowForwardIos size={12} />
      </button>
    </div>
  );
};

export default Pagination;
